import { type QueryClient } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { getPrivateWorkspaceAgentSessionGetKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import { applyWorkspaceAgentSessionToListCaches } from '~/components/v2/components/workspace/workspace-agent-panel/utils/patchPrivateWorkspaceAgentSessionsListCache';

export type RefreshWorkspaceAgentOpenSessionParams = {
  getActiveSessionId: () => string | null;
  queryClient: QueryClient;
  workspaceId: string;
};

/**
 * One session GET for the open chat. Writes the same query cache the elicitation hook reads, so
 * stall refresh must not also invalidate that query (that would GET the session twice).
 */
export const refreshWorkspaceAgentOpenSession = async ({
  getActiveSessionId,
  queryClient,
  workspaceId,
}: RefreshWorkspaceAgentOpenSessionParams): Promise<void> => {
  const sessionId = getActiveSessionId();

  if (sessionId === null) {
    return;
  }

  const session = await callAirApi(() =>
    Agents.Sessions.get({
      agentType: 'workspace',
      sessionId,
      workspaceId,
    }),
  );

  if (getActiveSessionId() !== sessionId) {
    return;
  }

  queryClient.setQueryData(getPrivateWorkspaceAgentSessionGetKey({ sessionId, workspaceId }), session);
  applyWorkspaceAgentSessionToListCaches(queryClient, workspaceId, session);
};
